/* KUTEÈ welcome popup behavior (spec §6 + engagement model 2026-09/11).
   Automatic shows (all theme settings, all go through one gate: canAutoShow):
   - First show of a visit: after `delay` seconds — not if the popup was
     closed less than `reshow_days` ago (an earlier visit).
   - Reshow after a close: on the next page view (`second_ask_pages` page
     views after the close; 0 = off) or `reshow_after_seconds` later on the
     same page (0 = off), whichever comes first.
   - Never within `reshow_min_gap_seconds` of a close; never via
     exit-intent on the page where it was closed.
   - At most `max_auto_shows` automatic shows per visit (a visit ends after
     30 min without a page view).
   - Desktop exit-intent: can trigger any show that the gate allows.
   - Aggressive mode (`aggressive_reshow_seconds` > 0): reopens that long
     after every close and ignores the per-visit cap and reshow_days; the
     gap and the exit-intent rule still apply.
   - Never after signup (ku_popup_signed). Storage blocked → teaser tab
     only, no automatic shows.
   - Teaser tab appears once the popup has been dismissed (or when automatic
     shows are off for this visitor) and opens it on click (not counted).
   - Tabs stay in sync (storage event); Back/bfcache re-checks everything.
   - Focus-trap, ESC to close, returns focus on close.
   - In the Theme Editor it always shows so merchants can style it. */
(() => {
  const DISMISS_KEY = 'ku_popup_dismissed_at'; // last close, any visit
  const SIGNED_KEY = 'ku_popup_signed';
  const VISIT_KEY = 'ku_popup_visit'; // {seen, views, shows, closedAt, closedView}
  const VISIT_IDLE_MS = 30 * 60 * 1000;
  const PAGE_RESHOW_MS = 1200;
  const CLOSE_FALLBACK_MS = 350;

  const popup = document.querySelector('[data-ku-popup]');
  if (!popup) return;

  const card = popup.querySelector('.ku-popup__card');
  const closeBtn = popup.querySelector('[data-ku-popup-close]');
  const overlay = popup.querySelector('.ku-popup__overlay');
  const tab = document.querySelector('[data-ku-popup-tab]');

  const int = (value, fallback) => {
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? fallback : n;
  };
  const data = popup.dataset;
  const enabled = data.enabled === 'true';
  const delayMs = int(data.delay, 0) * 1000;
  const reshowDays = int(data.reshowDays, 7) || 7;
  const showMobile = data.showMobile === 'true';
  const secondAskPages = int(data.secondAskPages, 1);
  const exitIntent = data.exitIntent === 'true';
  const aggressiveMs = int(data.aggressiveSeconds, 0) * 1000;
  const minGapMs = int(data.minGapSeconds, 10) * 1000;
  const reshowAfterMs = int(data.reshowAfterSeconds, 30) * 1000;
  const maxShows = int(data.maxShows, 2);
  const justSigned = popup.querySelector('[data-ku-popup-signed]') !== null;
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  // ---- storage -------------------------------------------------------------
  const storageOk = (() => {
    try {
      const probe = '__ku_popup_probe';
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      return true;
    } catch (e) {
      return false;
    }
  })();
  const safeGet = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  };
  const safeSet = (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* blocked — non-fatal */
    }
  };

  const isSigned = () => Boolean(safeGet(SIGNED_KEY));
  const lastClose = () => int(safeGet(DISMISS_KEY), 0);

  const readVisit = () => {
    let v = null;
    try {
      v = JSON.parse(safeGet(VISIT_KEY));
    } catch (e) {
      v = null;
    }
    if (!v || typeof v !== 'object' || Date.now() - (v.seen || 0) > VISIT_IDLE_MS) {
      v = { seen: Date.now(), views: 0, shows: 0, closedAt: 0, closedView: 0 };
    }
    return v;
  };
  const writeVisit = (v) => {
    v.seen = Date.now();
    safeSet(VISIT_KEY, JSON.stringify(v));
  };

  // ---- environment -----------------------------------------------------------
  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
  const autoAllowed = () => enabled && storageOk && (!isMobile() || showMobile);
  // Another overlay (reserve modal, cart/menu drawer) holds the page — never
  // stack the welcome popup on top of it; automatic shows just skip.
  const isBusy = () => document.body.classList.contains('overflow-hidden');

  // ---- per-page state ------------------------------------------------------------
  let state = 'closed'; // closed | open | closing
  let lastFocused = null;
  let thisView = 0; // this page view's number within the visit
  let closedHere = false; // closed during this page view
  let timers = [];
  let finishTimer = null;
  let exitArmed = false;

  const cancelTimers = () => {
    timers.forEach((id) => clearTimeout(id));
    timers = [];
  };

  const showTab = () => {
    if (tab && !isSigned()) tab.hidden = false;
  };
  const hideTab = () => {
    if (tab) tab.hidden = true;
  };

  // ---- the one gate every automatic show passes through -------------------
  // kind: 'first' (first show of the visit) | 'reshow' (after a close) |
  // 'exit' (exit-intent: either of the two).
  function canAutoShow(kind) {
    if (!autoAllowed() || state !== 'closed' || isSigned() || isBusy()) return false;
    if (kind === 'exit' && closedHere) return false;
    const now = Date.now();
    const closed = lastClose();
    if (closed && now - closed < minGapMs) return false;
    if (aggressiveMs > 0) return true;
    const v = readVisit();
    if (v.shows >= maxShows) return false;
    const first = v.shows === 0;
    if (kind === 'first' || (kind === 'exit' && first)) {
      return first && !(closed && now - closed < reshowDays * 24 * 60 * 60 * 1000);
    }
    return Boolean(v.closedAt); // a reshow needs a close in this visit
  }

  // Schedule an automatic show; never earlier than the gap after the last close.
  function schedule(kind, ms) {
    const closed = lastClose();
    const gapLeft = closed ? closed + minGapMs - Date.now() : 0;
    const id = setTimeout(() => {
      timers = timers.filter((t) => t !== id);
      if (canAutoShow(kind)) open({ auto: true });
    }, Math.max(ms, gapLeft, 0));
    timers.push(id);
  }

  // ---- open / close ------------------------------------------------------
  const focusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const trap = (event) => {
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = popup.querySelectorAll(focusableSelector);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  function open({ auto = false } = {}) {
    if (state === 'open') return;
    if (state === 'closing') {
      // Reopened mid fade-out: drop the pending finish so it can't re-hide us.
      clearTimeout(finishTimer);
      card.removeEventListener('transitionend', onCardTransitionEnd);
    } else {
      lastFocused = document.activeElement;
    }
    state = 'open';
    cancelTimers();
    if (auto) {
      const v = readVisit();
      v.shows += 1;
      writeVisit(v);
    }
    popup.removeAttribute('hidden');
    requestAnimationFrame(() => {
      if (state === 'open') popup.classList.add('is-open');
    });
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', trap);
    hideTab();
    // Focus the card itself (tabindex=-1): screen readers enter the dialog,
    // but no visible focus ring lands on the close button.
    if (card) card.focus();
  }

  function finishClose() {
    if (state !== 'closing') return;
    state = 'closed';
    clearTimeout(finishTimer);
    card.removeEventListener('transitionend', onCardTransitionEnd);
    popup.setAttribute('hidden', '');
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }
  // Only the card's own fade ends the close (slideshow images bubble theirs).
  function onCardTransitionEnd(event) {
    if (event.target === card) finishClose();
  }

  // record=false: closed because another tab dismissed/signed — that tab
  // already wrote the state.
  function close({ record = true } = {}) {
    if (state !== 'open') return;
    state = 'closing';
    popup.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', trap);
    card.addEventListener('transitionend', onCardTransitionEnd);
    finishTimer = setTimeout(finishClose, CLOSE_FALLBACK_MS);
    if (designMode) return;

    cancelTimers();
    disarmExit();
    closedHere = true;
    showTab();
    if (!record) return;

    const now = Date.now();
    safeSet(DISMISS_KEY, String(now));
    const v = readVisit();
    v.closedAt = now;
    v.closedView = thisView;
    writeVisit(v);
    const sameMs = aggressiveMs > 0 ? aggressiveMs : reshowAfterMs;
    if (sameMs > 0) schedule('reshow', sameMs);
  }

  // ---- exit-intent (desktop pointers) -----------------------------------
  function onMouseOut(event) {
    if (event.relatedTarget) return;
    if (event.clientY > 0) return;
    if (!canAutoShow('exit')) return;
    disarmExit(); // once per page view
    open({ auto: true });
  }
  function armExit() {
    if (exitArmed || !exitIntent || isMobile() || !window.matchMedia('(hover: hover)').matches) return;
    document.addEventListener('mouseout', onMouseOut);
    exitArmed = true;
  }
  function disarmExit() {
    document.removeEventListener('mouseout', onMouseOut);
    exitArmed = false;
  }

  // ---- page view: count it and plan this page's automatic shows ----------
  function pageView() {
    cancelTimers();
    closedHere = false;
    if (!enabled || isSigned()) {
      hideTab();
      return;
    }
    if (!autoAllowed()) {
      // Storage blocked, or phones with auto-show off: the tab is the only ask.
      showTab();
      return;
    }
    if (lastClose()) showTab();

    const v = readVisit();
    v.views += 1;
    thisView = v.views;
    writeVisit(v);

    armExit();
    if (state !== 'closed') return;
    if (aggressiveMs > 0) {
      schedule('first', delayMs); // gate ignores cap + reshow_days here
    } else if (v.shows === 0) {
      schedule('first', delayMs);
    } else if (v.closedAt && secondAskPages > 0 && thisView - v.closedView >= secondAskPages) {
      schedule('reshow', PAGE_RESHOW_MS);
    }
  }

  // ---- wiring --------------------------------------------------------------
  if (closeBtn) closeBtn.addEventListener('click', () => close());
  // Dismiss on any click outside the card — the overlay OR the dimmed
  // container area (robust regardless of overlay hit-testing).
  popup.addEventListener('click', (event) => {
    if (event.target === popup || event.target === overlay) close();
  });
  if (tab) tab.addEventListener('click', () => open());

  // Theme Editor: always preview.
  if (designMode) {
    open();
    return;
  }

  // Other tabs: a signup silences this one; a dismissal closes the popup here
  // too (it is the same ask). Pending shows re-check the gate when they fire.
  window.addEventListener('storage', (event) => {
    if (event.key === SIGNED_KEY && event.newValue) {
      cancelTimers();
      disarmExit();
      close({ record: false });
      hideTab();
    } else if (event.key === DISMISS_KEY && event.newValue) {
      close({ record: false });
    }
  });

  // Back/forward cache: a frozen page's timers would resume with stale
  // decisions. Drop them on the way out; on restore it is a new page view.
  window.addEventListener('pagehide', () => {
    cancelTimers();
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) pageView();
  });

  // A PDP waitlist signup just succeeded on this page load — its inline
  // confirmation is the message. Stay quiet now and forever (they're signed).
  if (document.querySelector('[data-ku-waitlist-signed]')) {
    safeSet(SIGNED_KEY, '1');
    hideTab();
    return;
  }

  // Success reload: keep the signed state, show the success card briefly.
  if (justSigned) {
    safeSet(SIGNED_KEY, '1');
    hideTab();
    open();
    return;
  }

  pageView();
})();
